import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReactionTimeGame = ({ 
  onGameComplete, 
  onExit,
  className = "" 
}) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, ready, active, result
  const [reactionTime, setReactionTime] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [currentAttempt, setCurrentAttempt] = useState(1);
  const [showStimulus, setShowStimulus] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isCountingDown, setIsCountingDown] = useState(false);
  
  const startTimeRef = useRef(null);
  const timeoutRef = useRef(null);
  const maxAttempts = 5;

  const startGame = () => {
    setGameState('ready');
    setIsCountingDown(true);
    setCountdown(3);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setIsCountingDown(false);
          startRound();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRound = () => {
    setGameState('active');
    setShowStimulus(false);
    
    // Random delay between 2-6 seconds
    const delay = Math.random() * 4000 + 2000;
    
    timeoutRef.current = setTimeout(() => {
      setShowStimulus(true);
      startTimeRef.current = Date.now();
    }, delay);
  };

  const handleReaction = () => {
    if (!showStimulus) {
      // Too early - false start
      clearTimeout(timeoutRef?.current);
      setGameState('waiting');
      alert('Too early! Wait for the green circle to appear.');
      return;
    }

    const endTime = Date.now();
    const reactionMs = endTime - startTimeRef?.current;
    setReactionTime(reactionMs);
    
    const newAttempts = [...attempts, reactionMs];
    setAttempts(newAttempts);
    
    if (currentAttempt >= maxAttempts) {
      // Game complete
      const averageTime = newAttempts?.reduce((a, b) => a + b, 0) / newAttempts?.length;
      const bestTime = Math.min(...newAttempts);
      
      setGameState('result');
      onGameComplete({
        averageReactionTime: Math.round(averageTime),
        bestReactionTime: bestTime,
        attempts: newAttempts,
        completedAt: new Date()?.toISOString(),
        gameType: 'reaction-time'
      });
    } else {
      // Next attempt
      setTimeout(() => {
        setCurrentAttempt(prev => prev + 1);
        setReactionTime(null);
        startRound();
      }, 2000);
    }
  };

  const resetGame = () => {
    setGameState('waiting');
    setAttempts([]);
    setCurrentAttempt(1);
    setReactionTime(null);
    setShowStimulus(false);
    setIsCountingDown(false);
    setCountdown(3);
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef?.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef?.current) {
        clearTimeout(timeoutRef?.current);
      }
    };
  }, []);

  const getReactionRating = (time) => {
    if (time < 200) return { rating: 'Excellent', color: 'text-success' };
    if (time < 300) return { rating: 'Good', color: 'text-primary' };
    if (time < 400) return { rating: 'Average', color: 'text-warning' };
    return { rating: 'Needs Practice', color: 'text-error' };
  };

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-card border-b border-border">
        <div className="flex items-center justify-between h-16 px-4">
          <div className="flex items-center space-x-3">
            <Icon name="Zap" size={24} color="var(--color-primary)" />
            <span className="text-lg font-semibold text-foreground">Reaction Time Test</span>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="text-sm text-muted-foreground">
              Attempt {currentAttempt} of {maxAttempts}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onExit}
              iconName="X"
            >
              Exit
            </Button>
          </div>
        </div>
      </div>
      {/* Game Area */}
      <div className="pt-16 min-h-screen flex items-center justify-center p-4">
        {gameState === 'waiting' && (
          <div className="text-center max-w-md">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Zap" size={48} color="var(--color-primary)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-4">Reaction Time Test</h2>
            <p className="text-muted-foreground mb-6">
              Test your reaction speed! When you see the green circle appear, tap or click as quickly as possible.
            </p>
            
            <div className="bg-card border border-border rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-foreground mb-2">Instructions:</h3>
              <ul className="text-sm text-muted-foreground text-left space-y-1">
                <li>• Wait for the countdown to finish</li>
                <li>• Watch for the green circle to appear</li>
                <li>• Click/tap as fast as you can when you see it</li>
                <li>• Complete 5 attempts for your average score</li>
              </ul>
            </div>
            
            <Button
              variant="default"
              size="lg"
              onClick={startGame}
              iconName="Play"
              iconPosition="left"
            >
              Start Test
            </Button>
          </div>
        )}

        {gameState === 'ready' && isCountingDown && (
          <div className="text-center">
            <div className="text-6xl font-bold text-primary mb-4">
              {countdown}
            </div>
            <p className="text-lg text-muted-foreground">Get ready...</p>
          </div>
        )}

        {gameState === 'active' && (
          <div className="text-center w-full max-w-2xl">
            <div 
              className="relative w-full h-96 bg-card border-2 border-dashed border-border rounded-lg flex items-center justify-center cursor-pointer transition-all duration-150 hover:bg-muted/50"
              onClick={handleReaction}
            >
              {showStimulus ? (
                <div className="w-32 h-32 bg-success rounded-full animate-pulse shadow-soft-lg">
                  <div className="w-full h-full bg-success/80 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-lg">CLICK!</span>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Icon name="Eye" size={48} color="var(--color-muted-foreground)" className="mb-4" />
                  <p className="text-lg text-muted-foreground">Wait for the green circle...</p>
                </div>
              )}
            </div>
            
            {reactionTime && (
              <div className="mt-6 p-4 bg-card border border-border rounded-lg">
                <div className="text-2xl font-bold text-primary mb-2">{reactionTime}ms</div>
                <div className={`text-sm font-medium ${getReactionRating(reactionTime)?.color}`}>
                  {getReactionRating(reactionTime)?.rating}
                </div>
              </div>
            )}
          </div>
        )}

        {gameState === 'result' && (
          <div className="text-center max-w-md">
            <div className="w-24 h-24 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Trophy" size={48} color="var(--color-success)" />
            </div>
            
            <h2 className="text-2xl font-bold text-foreground mb-6">Test Complete!</h2>
            
            <div className="space-y-4 mb-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Average Reaction Time</div>
                <div className="text-3xl font-bold text-primary">
                  {Math.round(attempts?.reduce((a, b) => a + b, 0) / attempts?.length)}ms
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-1">Best Time</div>
                <div className="text-xl font-semibold text-success">
                  {Math.min(...attempts)}ms
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="text-sm text-muted-foreground mb-2">All Attempts</div>
                <div className="flex flex-wrap gap-2 justify-center">
                  {attempts?.map((time, index) => (
                    <span 
                      key={index}
                      className="px-2 py-1 bg-muted rounded text-xs font-medium text-foreground"
                    >
                      {time}ms
                    </span>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={resetGame}
                iconName="RotateCcw"
                iconPosition="left"
              >
                Try Again
              </Button>
              <Button
                variant="default"
                onClick={onExit}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReactionTimeGame;